import React, { useState } from 'react';
import cn from 'classnames';

import { ROOM_STATUS, USER_ROLES } from 'constants/index';
import { setIsShowFooterPopUpOverlay } from 'client/redux/ui';
import { selectIsShowShares } from 'client/redux/ui/selectors';
import { selectRoomStatus } from 'client/redux/room/selectors';
import { selectUserName } from 'client/redux/client/selectors';
import { selectIsBroadcasting } from 'client/redux/mux-details/selectors';
import { useLiveStreamContext } from 'client/components/LiveStreamConnector/context';
import {
  useTranslation,
  useAppDispatch,
  useAppSelector,
  useToggleMicrophone,
  useReactionsCount,
  useGAEventCommonProps,
} from 'client/hooks';
import { selectOccupancy, selectRaiseHandUsersTotal, selectSignedUsersOccupancy } from 'client/redux/pubnub/selectors';
import { analytics } from 'client/utils/analytics';
import { eventBus } from 'client/utils/event-bus';

import { MicWarning } from '../../MicWarning';
import MicrophoneButton from './MicrophoneButton';
import SharesActions from '../../../SharesActions';
import Hints, { HintItemType } from '../../../Hints';
import ReactionsDetailsDrawer from '../../../Drawers/ReactionsDetailsDrawer';
import UsersStatisticMenu from '../../../Drawers/UsersStatisticsDrawer/UsersStatisticMenu';
import UsersStatisticsButton from '../../../Drawers/UsersStatisticsDrawer/UsersStatisticButton';
import ReactionsDetailsDrawerButton from '../../../Drawers/ReactionsDetailsDrawer/ReactionsDetailsDrawerButton';

import css from './SpeakerActionsPanel.module.scss';

interface Props {
  className?: string;
}

const SpeakerActionsPanel: React.FC<Props> = (props) => {
  const { className = '' } = props;
  const { common } = useGAEventCommonProps();

  const { state } = useLiveStreamContext();
  const { translate: translateHints } = useTranslation('hints');
  const { translate: translateActions } = useTranslation('actionsPanel');
  const { muteMic, unMuteMic } = useToggleMicrophone();
  const isBroadcasting = useAppSelector(selectIsBroadcasting);
  const roomStatus = useAppSelector(selectRoomStatus);
  const userName = useAppSelector(selectUserName);

  const dispatch = useAppDispatch();
  const isShowShares = useAppSelector(selectIsShowShares);

  const counterIncognito = useAppSelector(selectOccupancy);
  const pendingRequestsCount = useAppSelector(selectRaiseHandUsersTotal);
  const signedListenersCount = useAppSelector(selectSignedUsersOccupancy);

  const counter = counterIncognito + signedListenersCount;
  const { total: totalReactions } = useReactionsCount();

  const [isClosedHint, setIsClosedHint] = useState<boolean>(true);
  const [isMyMicMuted, setIsMyMicMuted] = useState<boolean>(true);
  const currentHintIndex = roomStatus === ROOM_STATUS.ACTIVE && isBroadcasting ? 1 : 0;
  const hints = React.useMemo(() => {
    const hintsData: HintItemType[] = [
      {
        icon: 'recording-helper',
        title: translateHints(`${USER_ROLES.SPEAKER}.initialHint.title`, { name: userName }),
        text: translateHints(`${USER_ROLES.SPEAKER}.initialHint.text`),
      },
      {
        icon: 'red_microphone',
        title: translateHints(`${USER_ROLES.SPEAKER}.activeRoomHint.title`, { name: userName }),
        text: translateHints(`${USER_ROLES.SPEAKER}.activeRoomHint.text`),
      },
    ];

    return hintsData;
  }, [translateHints]);

  const [isReactionsDrawerOpen, setIsReactionsDrawerOpen] = useState<boolean>(false);
  const [isShowStatisticDrawer, setIsShowStatisticDrawer] = useState<boolean>(false);

  React.useEffect(() => {
    if ((roomStatus === ROOM_STATUS.ACTIVE || isBroadcasting) && state.localParticipant) {
      dispatch(setIsShowFooterPopUpOverlay(true));
      setIsClosedHint(false);
    }
  }, [roomStatus, dispatch, isBroadcasting, state.localParticipant]);

  React.useEffect(() => {
    const unsubscribe = eventBus.subscribe<{ isMuted: boolean }>('TOGGLE_LOCAL_MIC', onToggleLocalMic);

    return () => {
      unsubscribe();
    };
  }, []);

  const onToggleLocalMic = (data?: { isMuted: boolean }) => {
    if (!data) return;
    setIsMyMicMuted(data.isMuted);
  };

  const broadcastConnectionHandler = async () => {
    if (isMyMicMuted) {
      console.info('Connect mic to broadcast');
      unMuteMic();
      onHintClose();

      analytics.gtag.event('audio_chat_content_click', {
        ...common,
        text_clicked: translateActions('btns.speakerMicUnmutedBtn'),
      });
    } else {
      console.info('Disconnect mic from broadcast');
      muteMic();

      analytics.gtag.event('audio_chat_content_click', {
        ...common,
        text_clicked: translateActions('btns.speakerMicMutedBtn'),
      });
    }
  };

  const onHintClose = () => {
    setIsClosedHint(true);
    dispatch(setIsShowFooterPopUpOverlay(false));
  };

  const statisticDrawerState = (state: boolean) => {
    if (state) {
      analytics.gtag.event('audio_chat_content_click', {
        ...common,
        text_clicked: translateActions('btns.usersStatisticBtn'),
      });
    }
    setIsShowStatisticDrawer(state);
  };

  const openReactionsDrawer = () => {
    if (!isReactionsDrawerOpen) {
      analytics.gtag.event('audio_chat_content_click', {
        ...common,
        text_clicked: translateActions('btns.detailedReactionsBtn'),
      });
      setIsReactionsDrawerOpen(true);
    }
  };

  const closeReactionsDrawer = () => {
    setIsReactionsDrawerOpen(false);
  };

  return (
    <div className={cn(css.speakerActionsPanel, className)}>
      <div className={cn(css.speakerActionsPanelI)}>
        <div className={cn(css.action, css.shares, isShowShares && css.bigestIndex)}>
          <SharesActions />
        </div>
        <div
          className={cn(css.action, css.micConnection, !state.localParticipant && css.disabled)}
          onClick={broadcastConnectionHandler}
        >
          <MicWarning size="large" className={css.micWarning} />
          <MicrophoneButton isMicMuted={isMyMicMuted} />
        </div>
        <div className={cn(css.action, css.reactions)}>
          <ReactionsDetailsDrawerButton
            totalReactions={totalReactions}
            openReactionsDrawer={openReactionsDrawer}
            isReactionsDrawerOpen={isReactionsDrawerOpen}
          />
        </div>
        <div className={cn(css.action, css.listenersDetails)}>
          <UsersStatisticsButton statisticDrawerState={statisticDrawerState} />
        </div>
      </div>

      {hints && !isClosedHint && <Hints hints={hints} next={currentHintIndex} handleClose={onHintClose} />}

      <ReactionsDetailsDrawer
        userRole={USER_ROLES.SPEAKER}
        visible={isReactionsDrawerOpen}
        handleClose={closeReactionsDrawer}
      />

      <UsersStatisticMenu
        visible={isShowStatisticDrawer}
        className={css.statsDrawer}
        totalListenersCount={counter}
        connectedListenersCount={signedListenersCount}
        pendingRequestsCount={pendingRequestsCount}
      />
    </div>
  );
};

export default SpeakerActionsPanel;
